import Grid from "@material-ui/core/Grid";
import classNames from "classnames";
import { UploadErrors } from "components/admin/color-swatches/upload-errors";
import { swatchesBulkUploadStyles } from "components/admin/color-swatches/upload-images-dialog";
import DoneIcon from "@material-ui/icons/Done";
import FileIcon from "@material-ui/icons/InsertDriveFileOutlined";
import Reminders from "components/product-media/components/reminders";
import { DragMessage } from "components/upload/components";
import { DropzoneUploader, withMediaStyles } from "components/upload/dropzone-uploader";
import {
    NotificationError,
    NotificationInfo,
    NotificationSuccess,
    useNotificationState,
} from "components/notification-banners";
import { format, formatMessage } from "lib/globalize-util";
import React, { useCallback, useEffect, useMemo } from "react";
import theme from "styles/theme";
import { Box, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { DATATEST } from "constants/integration";

interface IProps {
    onDrop?: (file: File | File[], onSettled?: (error: boolean) => void) => void;
    title?: string;
    hasError?: boolean;
    tips: {
        title?: string;
        items?: React.ReactFragment;
    };
    isUploading?: boolean;
    errorList?: string[];
    dragMessage?: string;
    maxFileSize?: number;
    multiple?: boolean;
    formatType: string;
    wasProcessed?: boolean;
    hasDataToChange?: boolean;
    errorMessage: React.ReactNode;
    callbackCloseAlert?: () => void;
    showSuccessMessage?: boolean;
    successMessage?: string;
    handleCallbackSuccessCloseAlert?: () => void;
}

export const EXCEL_FORMAT_TYPE =
    ".csv, .xls, .xlsx, text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel";
export const IMAGE_FORMAT_TYPE = "image/jpeg,image/jpg";

type DropzoneContentProps = {
    isUploading: boolean;
    showDoneIcon: boolean;
    dragMessage?: string;
};

const DropzoneContent = ({ isUploading, showDoneIcon, dragMessage }: DropzoneContentProps) => {
    if (isUploading) {
        return null;
    }

    const classes = swatchesBulkUploadStyles({});

    return (
        <>
            {showDoneIcon ? (
                <DoneIcon className={classNames(classes.icon, classes.iconSuccess)} />
            ) : (
                <FileIcon className={classes.icon} />
            )}
            <DragMessage path={dragMessage ?? "Drag your file here |br| or |span|click here|span| to select"} />
        </>
    );
};

/**
 * Uploader component that has file specifications & upload tips.
 */
export const UploaderWithTips: React.FunctionComponent<IProps> = ({
    title,
    tips = {},
    hasError = false,
    isUploading = false,
    errorList = [],
    dragMessage,
    maxFileSize,
    multiple = false,
    formatType,
    onDrop,
    wasProcessed,
    hasDataToChange,
    errorMessage,
    callbackCloseAlert,
    showSuccessMessage,
    successMessage,
    handleCallbackSuccessCloseAlert,
}) => {
    const [fileReceived, setFileReceived] = React.useState<boolean>(false);
    const [uploadError, handleError, hideError] = useNotificationState(
        format(
            "There was an error uploading your data. Review the errors below, make the necessary changes and try again."
        )
    );
    const classes = useStylesUploaderWithTipsList({});
    const fileProcessedWithErrors = React.useMemo(() => {
        return hasError && fileReceived && wasProcessed;
    }, [fileReceived, hasError, wasProcessed]);

    const getStylesForDropZoneBeforeFileProcessed = withMediaStyles();

    const getStylesForDropZoneAfterFileProcessed = fileProcessedWithErrors
        ? withMediaStyles({ border: `1px solid ${theme.palette.errorColor}` })
        : withMediaStyles({ border: `1px solid ${theme.palette.successAlertBorderColor}` });

    const dropzoneStyles = React.useMemo(() => {
        if (fileReceived && !isUploading && wasProcessed) {
            return getStylesForDropZoneAfterFileProcessed;
        }
        return getStylesForDropZoneBeforeFileProcessed;
    }, [
        fileReceived,
        getStylesForDropZoneAfterFileProcessed,
        getStylesForDropZoneBeforeFileProcessed,
        isUploading,
        wasProcessed,
    ]);

    const handleDrop = React.useCallback(
        (files: File[]) => {
            setFileReceived(false);

            if (files.length > 0) {
                onDrop?.(multiple ? files : files[0], (error) => {
                    error && handleError();
                    !error && hideError();
                });
                setFileReceived(true);
            }
        },
        [multiple, onDrop, handleError, hideError]
    );

    const handleCloseAlert = useCallback(() => {
        hideError();
        callbackCloseAlert?.();
        setFileReceived(false);
    }, [callbackCloseAlert, hideError]);

    useEffect(() => {
        if (errorMessage) {
            handleError();
        } else {
            hideError();
        }
    }, [errorMessage, handleError, hideError]);

    const showInformationAlert = useMemo(
        () => !fileProcessedWithErrors && !hasDataToChange,
        [fileProcessedWithErrors, hasDataToChange]
    );

    const showDoneIcon = useMemo(
        () => !fileProcessedWithErrors && fileReceived,
        [fileProcessedWithErrors, fileReceived]
    );

    return (
        <Box>
            <Typography
                component="h4"
                className={classes.title}
                gutterBottom
                data-test={DATATEST.ASSORTMENTS.ALLOCATION_DIMENSIONS.IMPORT_DOOR_GROUPINGS.UPLOADER.TITLE}
            >
                {title}
            </Typography>
            {showInformationAlert && (
                <NotificationInfo
                    message={errorMessage}
                    onClose={handleCloseAlert}
                    data-test={
                        DATATEST.ASSORTMENTS.ALLOCATION_DIMENSIONS.IMPORT_DOOR_GROUPINGS.UPLOADER.ALERT.INFORMATION
                    }
                />
            )}
            {!showInformationAlert && (
                <NotificationError
                    classes={{
                        root: classes.uploadErrorRoot,
                        icon: classes.errorIcon,
                    }}
                    message={errorMessage ?? uploadError}
                    onClose={handleCloseAlert}
                    data-test={DATATEST.ASSORTMENTS.ALLOCATION_DIMENSIONS.IMPORT_DOOR_GROUPINGS.UPLOADER.ALERT.ERROR}
                />
            )}

            {showSuccessMessage && (
                <NotificationSuccess
                    classes={{
                        root: classes.uploadSuccessRoot,
                        icon: classes.errorIcon,
                    }}
                    message={successMessage}
                    onClose={handleCallbackSuccessCloseAlert}
                    data-test={DATATEST.ASSORTMENTS.ALLOCATION_DIMENSIONS.IMPORT_DOOR_GROUPINGS.UPLOADER.ALERT.ERROR}
                />
            )}
            <Grid container wrap="nowrap" direction="row" spacing={3} alignItems="center" className={classes.marginTop}>
                <Grid item>
                    <DropzoneUploader
                        multiple={multiple}
                        loading={isUploading}
                        maxSize={maxFileSize}
                        accept={formatType}
                        onDrop={handleDrop}
                        onUploadError={handleError}
                        dataTest={
                            DATATEST.ASSORTMENTS.ALLOCATION_DIMENSIONS.IMPORT_DOOR_GROUPINGS.UPLOADER.DROPZONE_COMPONENT
                        }
                        {...dropzoneStyles}
                    >
                        <DropzoneContent
                            isUploading={isUploading}
                            showDoneIcon={showDoneIcon}
                            dragMessage={dragMessage}
                        />
                    </DropzoneUploader>
                </Grid>
                <Grid item>
                    <Reminders
                        title={tips.title ?? formatMessage("File specifications & upload tips:")}
                        shouldUseCurrentColor
                        dataTestList={
                            DATATEST.ASSORTMENTS.ALLOCATION_DIMENSIONS.IMPORT_DOOR_GROUPINGS.UPLOADER.REMINDER.LIST
                        }
                        dataTestTitle={
                            DATATEST.ASSORTMENTS.ALLOCATION_DIMENSIONS.IMPORT_DOOR_GROUPINGS.UPLOADER.REMINDER.TITLE
                        }
                    >
                        {tips.items}
                    </Reminders>
                </Grid>
                <UploadErrors errorList={errorList} />
            </Grid>
        </Box>
    );
};

export const useStylesUploaderWithTipsList = makeStyles({
    marginTop: {
        marginTop: 4,
    },
    title: {
        fontFamily: "Lato, sans-serif",
        fontWeight: 400,
        fontSize: 16,
        color: theme.palette.offBlack,
        marginBottom: 32,
    },
    errorIcon: {
        margin: 10,
    },
    uploadErrorRoot: {
        alignItems: "flex-start",
    },
    uploadSuccessRoot: {
        alignItems: "center",
        fontSize: 14,
    },
});
